import React, { useEffect, useRef, useState } from "react";

/**
 * จำคอลัมน์ที่ซ่อนไว้ต่อแท็บใน localStorage (ของเครื่องนี้เท่านั้น) key = ชื่อคอลัมน์ที่ซ่อน
 * อ่าน/เขียนใน try/catch เพราะโหมดส่วนตัว/บล็อกข้อมูลเว็บอาจทำให้ localStorage ใช้ไม่ได้ ต้องยังทำงานได้ปกติ
 */
export function useHiddenColumns(storageKey) {
  const [hidden, setHidden] = useState(() => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(storageKey) || "[]");
      return new Set(Array.isArray(saved) ? saved : []);
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify([...hidden]));
    } catch {
      // เก็บไม่ได้ก็แค่ไม่จำข้ามการรีเฟรช
    }
  }, [hidden, storageKey]);

  const toggle = (key) =>
    setHidden((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const reset = () => setHidden(new Set());
  const show = (key) => !hidden.has(key);

  return { hidden, show, toggle, reset };
}

/**
 * ปุ่ม "แสดงคอลัมน์" เปิดรายการติ๊กเลือกคอลัมน์ที่จะแสดงในตาราง
 * options: [{ key, label, group? }] · hidden: Set ของ key ที่ซ่อน · ปิดเมื่อคลิกนอกกล่องหรือกด Esc
 */
export default function ColumnPicker({ options, hidden, onToggle, onReset }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (event) => {
      if (!boxRef.current?.contains(event.target)) setOpen(false);
    };
    const handleKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const hiddenCount = options.filter((option) => hidden.has(option.key)).length;
  let lastGroup = null;

  return (
    <div className="cmru-colpick" ref={boxRef}>
      <button type="button" className={`btn stg-btn stg-btn--ghost ${hiddenCount ? "is-active" : ""}`} onClick={() => setOpen((value) => !value)} aria-expanded={open} aria-haspopup="true">
        <i className="fa-solid fa-eye" />แสดงคอลัมน์{hiddenCount ? <span className="cmru-colpick-badge">ซ่อน {hiddenCount}</span> : null}
      </button>
      {open && (
        <div className="cmru-colpick-menu" role="menu">
          <div className="cmru-colpick-head">
            <span>เลือกคอลัมน์ที่จะแสดง</span>
            <button type="button" className="cmru-colpick-reset" onClick={onReset} disabled={!hiddenCount}>แสดงทั้งหมด</button>
          </div>
          <div className="cmru-colpick-list">
            {options.map((option) => {
              const groupHead = option.group && option.group !== lastGroup ? <div className="cmru-colpick-group" key={`g-${option.group}`}>{option.group}</div> : null;
              lastGroup = option.group || lastGroup;
              return (
                <React.Fragment key={option.key}>
                  {groupHead}
                  <label className="cmru-colpick-item">
                    <input type="checkbox" checked={!hidden.has(option.key)} onChange={() => onToggle(option.key)} />
                    <span>{option.label}</span>
                  </label>
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
